export const NewZealandRegions = ['DEFAULT'] as const;
export const AustralianRegions = [
  'NSW',
  'QLD',
  'SA',
  'TAS',
  'VIC',
  'WA',
] as const;
export const GreatBritainRegions = ['DEFAULT'] as const;

export type GeneralRegion =
  | typeof NewZealandRegions[number]
  | typeof AustralianRegions[number]
  | typeof GreatBritainRegions[number];

export const ACTIVE_COUNTRIES = [
  {
    code: 'NZ',
    locale: 'en_NZ',
    regionKeys: [...NewZealandRegions],
    name: 'New Zealand',
    currency: 'NZD',
    isImperialSystem: false,
  },
  {
    code: 'AU',
    locale: 'en_AU',
    regionKeys: [...AustralianRegions],
    name: 'Australia',
    currency: 'AUD',
    isImperialSystem: false,
  },
  {
    code: 'GB',
    locale: 'en_GB',
    regionKeys: [...GreatBritainRegions],
    name: 'United Kingdom',
    currency: 'GBP',
    isImperialSystem: true,
  },
] as const;

export const ACTIVE_COUNTRY_CODES = ACTIVE_COUNTRIES.map(
  (country) => country.code,
);

export type ActiveCountryCode = typeof ACTIVE_COUNTRIES[number]['code'];

export const INACTIVE_COUNTRIES = [
  {
    code: 'AR',
    locale: 'es_AR',
    regionKeys: ['DEFAULT'],
    name: 'Argentina',
    currency: 'ARS',
  },
  {
    code: 'AT',
    locale: 'de_AT',
    regionKeys: ['DEFAULT'],
    name: 'Austria',
    currency: 'EUR',
  },
  {
    code: 'BE',
    locale: 'fr_BE',
    regionKeys: ['DEFAULT'],
    name: 'Belgium',
    currency: 'EUR',
  },
  {
    code: 'BR',
    locale: 'pt_BR',
    regionKeys: ['DEFAULT'],
    name: 'Brazil',
    currency: 'BRL',
  },
  {
    code: 'CA',
    locale: 'en_CA',
    regionKeys: ['DEFAULT'],
    name: 'Canada',
    currency: 'CAD',
  },
  {
    code: 'CARIBBEAN',
    locale: 'es_ES',
    regionKeys: ['DEFAULT'],
    name: 'Caribbean Region',
    currency: 'USD', // :shrug: Caribbean isn't a country but apparently this currency is popular in the area
  },
  {
    code: 'CL',
    locale: 'es_CL',
    regionKeys: ['DEFAULT'],
    name: 'Chile',
    currency: 'CLP',
  },
  {
    code: 'CN',
    locale: 'zh_CN',
    regionKeys: ['DEFAULT'],
    name: 'China',
    currency: 'CNY',
  },
  {
    code: 'HK',
    locale: 'zh_HK',
    regionKeys: ['DEFAULT'],
    name: 'Hong Kong',
    currency: 'HKD',
  },
  {
    code: 'CO',
    locale: 'es_CO',
    regionKeys: ['DEFAULT'],
    name: 'Colombia',
    currency: 'COP',
  },
  {
    code: 'CR',
    locale: 'es_CR',
    regionKeys: ['DEFAULT'],
    name: 'Costa Rica',
    currency: 'CRC',
  },
  {
    code: 'CZ',
    locale: 'cs',
    regionKeys: ['DEFAULT'],
    name: 'Czech Republic',
    currency: 'CZK',
  },
  {
    code: 'EG',
    locale: 'ar_EG',
    regionKeys: ['DEFAULT'],
    name: 'Egypt',
    currency: 'EGP',
  },
  {
    code: 'FR',
    locale: 'fr_FR',
    regionKeys: ['DEFAULT'],
    name: 'France',
    currency: 'EUR',
  },
  {
    code: 'DE',
    locale: 'de_DE',
    regionKeys: ['DEFAULT'],
    name: 'Germany',
    currency: 'EUR',
  },
  {
    code: 'IN',
    locale: 'hi_IN',
    regionKeys: ['DEFAULT'],
    name: 'India',
    currency: 'INR',
  },
  {
    code: 'ID',
    locale: 'in_ID',
    regionKeys: ['DEFAULT'],
    name: 'Indonesia',
    currency: 'IDR',
  },
  {
    code: 'IE',
    locale: 'en_IE',
    regionKeys: ['DEFAULT'],
    name: 'Ireland',
    currency: 'EUR',
  },
  {
    code: 'IT',
    locale: 'it_IT',
    regionKeys: ['DEFAULT'],
    name: 'Italy',
    currency: 'EUR',
  },
  {
    code: 'JP',
    locale: 'ja_JP',
    regionKeys: ['DEFAULT'],
    name: 'Japan',
    currency: 'JPY',
  },
  {
    code: 'JO',
    locale: 'ar_JO',
    regionKeys: ['DEFAULT'],
    name: 'Jordan',
    currency: 'JOD',
  },
  {
    code: 'MY',
    locale: 'ms_MY',
    regionKeys: ['DEFAULT'],
    name: 'Malaysia',
    currency: 'MYR',
  },
  {
    code: 'MX',
    locale: 'es_MX',
    regionKeys: ['DEFAULT'],
    name: 'Mexico',
    currency: 'MXN',
  },
  {
    code: 'NL',
    locale: 'nl_NL',
    regionKeys: ['DEFAULT'],
    name: 'Netherlands',
    currency: 'EUR',
  },
  {
    code: 'PA',
    locale: 'es_PA',
    regionKeys: ['DEFAULT'],
    name: 'Panama',
    currency: 'USD',
  },
  {
    code: 'PL',
    locale: 'pl_PL',
    regionKeys: ['DEFAULT'],
    name: 'Poland',
    currency: 'PLN',
  },
  {
    code: 'PT',
    locale: 'pt_PT',
    regionKeys: ['DEFAULT'],
    name: 'Portugal',
    currency: 'EUR',
  },
  {
    code: 'QA',
    locale: 'ar_QA',
    regionKeys: ['DEFAULT'],
    name: 'Qatar',
    currency: 'QAR',
  },
  {
    code: 'RU',
    locale: 'ru',
    regionKeys: ['DEFAULT'],
    name: 'Russian Federation',
    currency: 'RUB',
  },
  {
    code: 'SA',
    locale: 'ar_SA',
    regionKeys: ['DEFAULT'],
    name: 'Saudi Arabia',
    currency: 'SAR',
  },
  {
    code: 'SG',
    locale: 'zh_SG',
    regionKeys: ['DEFAULT'],
    name: 'Singapore',
    currency: 'SGD',
  },
  {
    code: 'ZA',
    locale: 'en_ZA',
    regionKeys: ['DEFAULT'],
    name: 'South Africa',
    currency: 'ZAR',
  },
  {
    code: 'KR',
    locale: 'ko_KR',
    regionKeys: ['DEFAULT'],
    name: 'South Korea',
    currency: 'KRW',
  },
  {
    code: 'ES',
    locale: 'es_ES',
    regionKeys: ['DEFAULT'],
    name: 'Spain',
    currency: 'EUR',
  },
  {
    code: 'CH',
    locale: 'de_CH',
    regionKeys: ['DEFAULT'],
    name: 'Switzerland',
    currency: 'CHF',
  },
  {
    code: 'TH',
    locale: 'th_TH',
    regionKeys: ['DEFAULT'],
    name: 'Thailand',
    currency: 'THB',
  },
  {
    code: 'TR',
    locale: 'tr_TR',
    regionKeys: ['DEFAULT'],
    name: 'Turkey',
    currency: 'TRY',
  },
  {
    code: 'AE',
    locale: 'ar_AE',
    regionKeys: ['DEFAULT'],
    name: 'United Arab Emirates',
    currency: 'AED',
  },
  {
    code: 'US',
    locale: 'en_US',
    regionKeys: ['DEFAULT'],
    name: 'United States of America',
    currency: 'USD',
  },
  {
    code: 'VN',
    locale: 'vi_VN',
    regionKeys: ['DEFAULT'],
    name: 'Vietnam',
    currency: 'VND',
  },
] as const;

export const INACTIVE_COUNTRY_CODES = INACTIVE_COUNTRIES.map(
  (country) => country.code,
);

export type InactiveCountryCode = typeof INACTIVE_COUNTRIES[number]['code'];
